import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useEffect } from 'react';
import { useBaseStore } from '@roc/feature-app-core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useBorrowersStore } from '@roc/feature-borrowers';
import { BusinessOutlined, FileCopyOutlined, KeyboardArrowRight } from '@material-ui/icons';
import { useLoanStore } from '@roc/feature-loans';
import { CopyText, StandardDialog } from '@roc/ui';
import BorrowerOverview from '../borrowerOverview'
import EntityOverview from '../entityOverview';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { colors } from '../../utils/todoConstants'
import { CustomCard } from '../customCard';
import {
  formatPercentageValue,
  getValueOrDefaultDash,
} from 'libs/feature-one-tool/src/quote/utils/utils';
import WarningIcon from '@material-ui/icons/Warning';
import { formatBorrowerExperience } from '@roc/feature-utils';
const useStyles = makeStyles((theme: Theme) => ({
  borrowerText2: {
    fontSize: 13,
    lineHeight: '18px',
    color: '#5E69A6',
  },
  // one clickable row per entity / borrower, separated by dividers
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1),
    borderRadius: 12,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F7F8FC',
    },
  },
  rowDivider: {
    borderTop: '1px solid #E7EAF8',
  },
  avatar: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E7EAF8',
    color: '#34395E',
    fontSize: 13,
    fontWeight: 600,
    '& svg': {
      fontSize: 18,
    },
  },
  rowName: {
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 600,
    color: '#1B1D34',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  copyIcon: {
    fontSize: 13,
    marginLeft: 4,
    verticalAlign: 'middle',
  },
  dialogTitle: {
    fontSize: '34px',
    marginBottom: '-16px',
    fontWeight: 400,
    marginLeft: '30px',
    marginTop: '24px',
  },
  arrowRight: {
    fontSize: 20,
    color: '#9AA1B9',
  },
  hoverLink: {
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer'
    },
  },
  copyText: {
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      background: theme.palette.grey[200],
      border: `1px solid ${theme.palette.grey[200]}`,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderRadius: 16,
      '&::after': {
        display: 'inherit',
      },
    },
    '&::after': {
      display: 'none',
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
    },
  },
}));

export const formatText = (value, type) => {
  if (value === null || value === undefined) {
    return '-';
  }
  else {
    return `${value} ${type}`
  }
};

export const BorrowerSummaryCard = observer(() => {
  const classes = useStyles();
  const { loanStore } = useLoanStore();
  const { loanDetails } = loanStore;
  const { borrowersStore } = useBorrowersStore();
  const { borrowerSetupStore } = borrowersStore;
  const [borrower, setBorrower] = useState(null);
  const [showMoreInformationDialog, setShowMoreInformationDialog] = useState(
    false
  );
  const [type, setType] = useState('borrower');
  const entity = loanDetails?.borrowerEntity;
  const loanBorrowers = loanDetails?.borrowers;

  useEffect(() => {
    if (borrower?.borrowerId) {
      borrowerSetupStore.init(borrower, true, loanDetails.loanType)
      // borrowersStore.getBorrowerExposureGrid(borrower.borrowerId);
    }
  }, [borrower?.borrowerId]);

  return (
    <CustomCard
      paperNoShadow
      paperStyle={{
        height: '100%',
      }}
      title="Borrower Summary"
    >
      <Box>
        {(entity.ein ?? entity?.company?.einNumber) && (
          <EntityCard setShowMoreInformationDialog={setShowMoreInformationDialog} setType={setType} entity={entity} />
        )}
        {loanDetails?.borrowers?.map?.((borrower, index) => (
          <Box
            key={borrower?.borrowerId ?? index}
            className={index > 0 || (entity.ein ?? entity?.company?.einNumber) ? classes.rowDivider : undefined}
          >
            <BorrowerCard
              setShowMoreInformationDialog={setShowMoreInformationDialog}
              setType={setType}
              borrower={borrower}
              setBorrower={setBorrower}
              name={borrower?.fullName}
            />
          </Box>
        ))}
      </Box>
      <MoreInformationDialog
        open={showMoreInformationDialog}
        entity={entity}
        loanBorrower={borrower}
        loanBorrowers={loanBorrowers}
        currentBorrower={borrowerSetupStore?.currentBorrower}
        onClose={() => setShowMoreInformationDialog(false)}
        type={type}
      />
    </CustomCard>
  );
});

const BorrowerCard = observer(({ name, borrower, setShowMoreInformationDialog, setType, setBorrower }) => {
  const classes = useStyles();
  const { globalStore } = useBaseStore();
  const handleClick = () => {
    setBorrower(borrower);
    setType('borrower');
    setShowMoreInformationDialog(true);
  }

  const pgText: any = borrower.personalGuarantor ? 'PG' : 'Non PG';
  const initials = `${borrower?.firstName?.[0] ?? ''}${borrower?.lastName?.[0] ?? ''}`.toUpperCase() || name?.[0]?.toUpperCase();
  return (
    <Box onClick={handleClick} className={classes.summaryRow}>
      <Box className={classes.avatar}>{initials}</Box>
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center">
          <Typography className={classes.rowName}>{name}</Typography>
          {(borrower.exposureMonitoring && borrower.exposureMonitoring !== 'No Exposure') && (
            <Tooltip title={<Typography variant='subtitle1'>
              {borrower.exposureMessage}
            </Typography>}>
              <WarningIcon style={{ color: '#fdaf3d', fontSize: 18, marginLeft: 6 }} />
            </Tooltip>
          )}
        </Box>
        <Typography className={classes.borrowerText2}>{pgText} • {formatText(formatBorrowerExperience(borrower?.experience), 'exp')} • {formatText(borrower.medianScore ?? borrower.ficoProvidedAtOrigination, 'fico')} • {formatPercentageValue(borrower?.pctOwnership)}
        </Typography>
        {borrower?.emailAddress && (
          // stop the click so copying the email doesn't also open the borrower dialog
          <Box onClick={e => e.stopPropagation()} display="inline-block">
            <CopyText propStyles={classes}>
              <Typography className={`${classes.borrowerText2} ${classes.hoverLink}`}>
                {borrower?.emailAddress}
                <FileCopyOutlined className={classes.copyIcon} />
              </Typography>
            </CopyText>
          </Box>
        )}
      </Box>
      <KeyboardArrowRight className={classes.arrowRight} />
    </Box>
  );
});

const EntityCard = observer(({ entity, setShowMoreInformationDialog, setType }) => {
  const classes = useStyles();
  const handleClick = () => {
    setType('entity');
    setShowMoreInformationDialog(true);
  }
  return (
    <Box onClick={handleClick} className={classes.summaryRow}>
      <Box className={classes.avatar}><BusinessOutlined /></Box>
      <Box flex={1} minWidth={0}>
        <Typography className={classes.rowName}>{getValueOrDefaultDash(entity.company?.name)}</Typography>
        <Typography className={classes.borrowerText2}>Entity • EIN {getValueOrDefaultDash(entity.ein ?? entity?.company?.einNumber)}</Typography>
      </Box>
      <KeyboardArrowRight className={classes.arrowRight} />
    </Box>
  );
});

const MoreInformationDialog = observer(({ open, currentBorrower, onClose, type, entity, loanBorrower, loanBorrowers }) => {
  const classes = useStyles();
  const { loanStore } = useLoanStore();


  const dialogContent = type === 'entity' ?
    <EntityOverview entity={entity} loanBorrowers={loanBorrowers} /> : <BorrowerOverview loan={loanStore?.loanDetails} borrower={currentBorrower} loanBorrower={loanBorrower}
    />

  const handleClose = () => {
    loanStore.selectBorrowersStore.reset();
    loanStore.setExistingBorrowers();
    onClose();
  };
  return (
    <div>
      <StandardDialog
        title={<Typography className={classes.dialogTitle}>{type === 'entity' ? entity?.company?.name : `${loanBorrower?.firstName} ${loanBorrower?.lastName}`}</Typography>}
        open={open}
        maxWidth="xl"
        fullWidth={false}
        dialogContent={dialogContent}
        handleClose={handleClose}
        onClose={handleClose}
      /></div>
  );
});
